<script lang="ts" module>
  import type { schema } from '#lib/server/drizzle/index.js';
  import { fade } from 'svelte/transition';

  export type FePortMultiEdit = {
    type: 'fePort';
    item: Partial<Omit<typeof schema.fePort.$inferSelect, 'id' | 'name'>> & {
      name: string;
      ids: number[];
    };
  };
  export type ChannelBoardMultiEdit = {
    type: 'channelBoard';
    item: Partial<Omit<typeof schema.channelBoard.$inferSelect, 'id' | 'name'>> & {
      name: string;
      ids: number[];
    };
  };
  export type ControllerMultiEdit = {
    type: 'controller';
    item: Partial<Omit<typeof schema.controller.$inferSelect, 'id' | 'name'>> & {
      name: string;
      ids: number[];
    };
  };
  export type DkcMultiEdit = {
    type: 'dkc';
    item: Partial<Omit<typeof schema.dkc.$inferSelect, 'id' | 'name'>> & {
      name: string;
      ids: number[];
    };
  };
  export type HpMultiEditInfo =
    ChannelBoardMultiEdit | ControllerMultiEdit | DkcMultiEdit | FePortMultiEdit;
</script>

<script lang="ts">
  import { applyAction, enhance } from '$app/forms';
  import { refreshAll } from '$app/navigation';
  import Modal from '#lib/components/Modal.svelte';
  import { hpAddNames } from './HpAddModal.svelte';
  import FloatingLabel from '#lib/components/FloatingLabel.svelte';
  import MultiCheckbox from './MultiCheckbox.svelte';

  let formLoading = $state(false);
  let open = $state(false);
  let parentActive = $state(false);
  let info = $state<HpMultiEditInfo>({
    type: 'dkc',
    item: {
      ids: [],
      name: 'Placeholder',
      active: true,
    },
  });

  let manyItems = $derived(info && info.item.ids.length > 1);

  export function openModal(_info: HpMultiEditInfo, _parentActive: boolean) {
    info = _info;
    parentActive = _parentActive;
    open = true;
  }
</script>

{#if formLoading}
  <div
    class="fixed inset-0 z-5000 flex items-center justify-center bg-gray-500/60 backdrop-blur-xs"
    transition:fade|global
  >
    <div class="loading loading-spinner loading-xl"></div>
  </div>
{/if}
<form
  method="post"
  action="?/deleteHpSub"
  id="deleteForm"
  use:enhance={() => {
    formLoading = true;
    return async ({ result }) => {
      formLoading = true;
      await applyAction(result);

      if (result.type === 'success') {
        await refreshAll();
        formLoading = false;
        open = false;
      }
    };
  }}
>
  <input hidden type="text" name="type" value={info.type} />
  <input hidden type="text" name="ids" value={info.item.ids} />
</form>

<Modal
  header="Edit {info.item.ids.length > 1
    ? `${info.item.ids.length} ${hpAddNames[info.type]}s`
    : `${hpAddNames[info.type]} '${info.item.name}'`}"
  bind:open
>
  <form
    class="space-y-4"
    method="post"
    action="?/editHpSub"
    use:enhance={({ formData }) => {
      formLoading = true;

      if (info.item.active === false) formData.set('active', 'false');
      if ('automation' in info.item && info.item.automation === false)
        formData.set('automation', 'false');

      if (manyItems) {
        if (formData.get('automation') === 'on') formData.delete('automation');
        if (formData.get('active') === 'on') formData.delete('active');
      }

      return async ({ result }) => {
        await applyAction(result);

        if (result.type === 'success') {
          await refreshAll();
          formLoading = false;
          open = false;
        }
        //else if (result.type === 'failure') error = result.data?.error ?? {};
      };
    }}
  >
    <input hidden type="text" name="type" value={info.type} />
    <input hidden type="text" name="ids" value={info.item.ids} />

    <fieldset class="grid grid-cols-2 gap-4">
      <FloatingLabel title="Name">
        {#snippet input(placeholder)}
          <input
            type="text"
            name="name"
            class="input"
            {placeholder}
            bind:value={info.item.name}
            disabled={manyItems}
            title={manyItems ? 'Disabled for multiple items' : undefined}
          />
        {/snippet}
      </FloatingLabel>
      <MultiCheckbox
        name="active"
        title="Active"
        {manyItems}
        disabled={!parentActive}
        bind:value={info.item.active}
      />
      {#if info.type === 'channelBoard'}
        {@const { item } = info}
        <FloatingLabel title="Speed (Gb / s)">
          {#snippet input(placeholder)}
            <input
              type="number"
              name="speed"
              class="input"
              min="0"
              max="128"
              {placeholder}
              bind:value={item.speed}
            />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="Description">
          {#snippet input(placeholder)}
            <input name="description" class="input" {placeholder} bind:value={item.description} />
          {/snippet}
        </FloatingLabel>
      {:else if info.type === 'fePort'}
        {@const { item } = info}
        <FloatingLabel title="Speed">
          {#snippet input(placeholder)}
            <input
              type="number"
              name="speed"
              class="input"
              {placeholder}
              min="0"
              max="128"
              step="any"
              bind:value={item.speed}
            />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="Throughput">
          {#snippet input(placeholder)}
            <input
              type="number"
              class="input"
              {placeholder}
              step="any"
              min="0"
              name="throughput"
              bind:value={item.throughput}
            />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="Cables">
          {#snippet input(placeholder)}
            <input name="cables" class="input" {placeholder} bind:value={item.cables} />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="Switch">
          {#snippet input(placeholder)}
            <input name="switch" class="input" {placeholder} bind:value={item.switch} />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="Slot/Port">
          {#snippet input(placeholder)}
            <input name="slot" class="input" {placeholder} bind:value={item.slot} />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="Description">
          {#snippet input(placeholder)}
            <input name="note" class="input" {placeholder} bind:value={item.note} />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="Covers the room">
          {#snippet input(placeholder)}
            <input name="covers" class="input" {placeholder} bind:value={item.covers} />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="SAN Env">
          {#snippet input(placeholder)}
            <input name="san_env" class="input" {placeholder} bind:value={item.san_env} />
          {/snippet}
        </FloatingLabel>
        <FloatingLabel title="WWN">
          {#snippet input(placeholder)}
            <input name="wwn" class="input" {placeholder} bind:value={item.wwn} />
          {/snippet}
        </FloatingLabel>
        <MultiCheckbox
          name="automation"
          title="Automation"
          {manyItems}
          bind:value={item.automation}
        />
      {/if}
    </fieldset>

    <div class="modal-action">
      <button class="btn btn-error mr-auto" type="submit" form="deleteForm"> Delete </button>
      <button class="btn btn-secondary btn-ghost" type="button" onclick={() => (open = false)}>
        Cancel
      </button>
      <button class="btn btn-primary" type="submit"> Save </button>
    </div>
  </form>
</Modal>
